/**
 * Centralized Query Keys Management
 * 
 * Benefits:
 * - Type-safe query keys
 * - Easy invalidation
 * - Consistent naming
 * - Better debugging
 */

export const queryKeys = {
  // Portfolio queries
  portfolio: {
    all: ['portfolio'] as const,
    data: (userId: string, forceLab?: string) => 
      [...queryKeys.portfolio.all, 'data', userId, forceLab] as const,
    apps: (userId: string) => 
      [...queryKeys.portfolio.all, 'apps', userId] as const,
    assessments: (userId: string, forceLab?: string) => 
      [...queryKeys.portfolio.all, 'assessments', userId, forceLab] as const,
    labDefinitions: () => 
      [...queryKeys.portfolio.all, 'lab-definitions'] as const,
  },

  // Action Tickets queries
  tickets: {
    all: ['tickets'] as const,
    lists: () => [...queryKeys.tickets.all, 'list'] as const,
    list: (userId: string, appId?: string) => 
      [...queryKeys.tickets.lists(), userId, appId] as const,
    detail: (id: string) => 
      [...queryKeys.tickets.all, 'detail', id] as const,
    progress: (initiativeId: string) => 
      [...queryKeys.tickets.all, 'progress', initiativeId] as const,
    byProvider: (providerName: string) => 
      [...queryKeys.tickets.all, 'provider', providerName] as const,
    byStatus: (status: string) => 
      [...queryKeys.tickets.all, 'status', status] as const,
  },

  // Messages/Comments queries
  messages: {
    all: ['messages'] as const,
    byTicket: (ticketId: string) => 
      [...queryKeys.messages.all, 'ticket', ticketId] as const,
    internal: (ticketId: string) => 
      [...queryKeys.messages.byTicket(ticketId), 'internal'] as const,
    external: (ticketId: string) => 
      [...queryKeys.messages.byTicket(ticketId), 'external'] as const,
  },

  // Apps queries
  apps: {
    all: ['apps'] as const,
    lists: () => [...queryKeys.apps.all, 'list'] as const,
    accessible: (userId: string) => 
      [...queryKeys.apps.lists(), 'accessible', userId] as const,
    detail: (id: string) => 
      [...queryKeys.apps.all, 'detail', id] as const,
    assessments: (appId: string) => 
      [...queryKeys.apps.detail(appId), 'assessments'] as const,
  },

  // User queries
  users: {
    all: ['users'] as const,
    profile: (userId: string) => 
      [...queryKeys.users.all, 'profile', userId] as const,
    displayInfo: (userIds: string[]) => 
      [...queryKeys.users.all, 'display-info', userIds] as const,
    teamMembers: () => 
      [...queryKeys.users.all, 'team-members'] as const,
  },

  // Initiatives queries
  initiatives: {
    all: ['initiatives'] as const,
    byLab: (labId: string) => 
      [...queryKeys.initiatives.all, 'lab', labId] as const,
    detail: (id: string) => 
      [...queryKeys.initiatives.all, 'detail', id] as const,
  },

  // Provider queries
  providers: {
    all: ['providers'] as const,
    lists: () => [...queryKeys.providers.all, 'list'] as const,
    detail: (id: string) => 
      [...queryKeys.providers.all, 'detail', id] as const,
    estimates: (ticketId: string) => 
      [...queryKeys.providers.all, 'estimates', ticketId] as const,
  },

  // Notifications queries
  notifications: {
    all: ['notifications'] as const,
    list: (userId: string | undefined, isProviderManager: boolean) =>
      [...queryKeys.notifications.all, 'list', userId, isProviderManager] as const,
    unread: (userId: string) => 
      [...queryKeys.notifications.all, 'unread', userId] as const,
    byType: (type: string) => 
      [...queryKeys.notifications.all, 'type', type] as const,
  },

  // Reports queries
  reports: {
    all: ['reports'] as const,
    byType: (type: string) => 
      [...queryKeys.reports.all, 'type', type] as const,
    byApp: (appId: string) => 
      [...queryKeys.reports.all, 'app', appId] as const,
  },

  // Deals queries
  deals: {
    all: ['deals'] as const,
    lists: () => [...queryKeys.deals.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.deals.all, 'detail', id] as const,
    comments: (dealId: string) => [...queryKeys.deals.detail(dealId), 'comments'] as const,
    notifications: (userId: string | undefined) =>
      [...queryKeys.deals.all, 'notifications', userId] as const,
  },

  // Watchtower queries
  watchtower: {
    all: ['watchtower'] as const,
    alerts: (userId: string) => 
      [...queryKeys.watchtower.all, 'alerts', userId] as const,
  },

  // Creative Studio queries
  creative: {
    all: ['creative'] as const,
    assets: () => [...queryKeys.creative.all, 'assets'] as const,
    requests: () => [...queryKeys.creative.all, 'requests'] as const,
    aiRequests: () => [...queryKeys.creative.all, 'ai-requests'] as const,
  },
} as const;

/**
 * Helper function to invalidate related queries
 * 
 * Usage:
 * ```typescript
 * queryInvalidation.onTicketUpdate(queryClient, ticketId);
 * ```
 */
export const queryInvalidation = {
  // When a ticket is created
  onTicketCreate: (queryClient: any, userId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.portfolio.all });
  },

  // When a ticket is updated
  onTicketUpdate: (queryClient: any, ticketId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.messages.byTicket(ticketId) });
  },

  // When a comment/message is added
  onCommentCreate: (queryClient: any, ticketId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.messages.byTicket(ticketId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
  },

  // When an app is updated
  onAppUpdate: (queryClient: any, appId: string, userId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.apps.detail(appId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.apps.accessible(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.portfolio.all });
  },

  // When assessments change
  onAssessmentChange: (queryClient: any, appId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.apps.assessments(appId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.portfolio.all });
  },

  // When a deal is updated
  onDealUpdate: (queryClient: any, dealId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.deals.detail(dealId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.deals.lists() });
  },

  // When provider estimate changes
  onProviderEstimateChange: (queryClient: any, ticketId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.providers.estimates(ticketId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
  },
};
